import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';

import { AppModal } from '../../components/AppModal';
import { leadDetailPath, projectsListPath } from '../../utils/leadRoutes';
import { useModuleAccess } from '../../hooks/useModuleAccess';

export type LeadProjectRow = {
  id: string;
  display_id?: string | null;
  lead_title?: string | null;
  status?: string;
  created_at?: string;
  site_address?: string | null;
  offer_net_total?: number | null;
  offer_gross_total?: number | null;
  /** Rolle des Kontakts am Projekt (API: GET /contacts/:id/leads). */
  contact_roles?: Array<'project_contact' | 'site_contact'>;
};

const PREVIEW_LIMIT = 10;

const formatCurrency = (v: number | null | undefined): string =>
  v == null
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

const formatDateDe = (s: string | null | undefined): string =>
  s ? new Date(s).toLocaleDateString('de-DE') : '—';

/** Eine Farbe pro Status-String: stabil über Hash, keine semantischen Sonderfälle. */
const STATUS_COLOR_PALETTE = [
  'bg-sky-100 text-sky-800',
  'bg-violet-100 text-violet-800',
  'bg-teal-100 text-teal-800',
  'bg-orange-100 text-orange-800',
  'bg-fuchsia-100 text-fuchsia-800',
  'bg-lime-100 text-lime-800',
  'bg-cyan-100 text-cyan-900',
  'bg-pink-100 text-pink-800',
  'bg-amber-100 text-amber-900',
  'bg-indigo-100 text-indigo-800',
  'bg-emerald-100 text-emerald-900',
  'bg-rose-100 text-rose-800',
] as const;

function hashStatusKey(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function leadProjectStatusBadgeClasses(status: string | undefined | null): string {
  const raw = ((status ?? 'Neu').trim() || 'Neu').toLowerCase();
  const idx = hashStatusKey(raw) % STATUS_COLOR_PALETTE.length;
  return STATUS_COLOR_PALETTE[idx];
}

function formatMetaSecondaryLine(l: LeadProjectRow): string {
  const id = l.display_id ?? '';
  const date = formatDateDe(l.created_at);
  const addr = (l.site_address ?? '').trim();
  const roles = l.contact_roles ?? [];
  const roleLabel =
    roles.includes('site_contact') && roles.includes('project_contact')
      ? 'Auftraggeber · Baustellenkontakt'
      : roles.includes('site_contact')
        ? 'Baustellenkontakt'
        : '';
  const parts = [id, date, roleLabel].filter(Boolean);
  const head = parts.join(' · ');
  if (!addr) return head;
  return head ? `${head} · ${addr}` : addr;
}

/** Desktop: feste Spalten für Status und Beträge. */
const LEAD_ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_7rem_8rem] grid-rows-[auto_auto] gap-x-3 gap-y-0.5 items-start';

function LeadProjectRowInner({
  l,
  onClick,
  className,
  disabled,
}: {
  l: LeadProjectRow;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}) {
  const gross = l.offer_gross_total;
  const net = l.offer_net_total;
  const hasGross = gross != null;
  const hasNet = net != null;
  const hasPrice = hasGross || hasNet;
  const statusBadge = (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap max-w-full truncate ${leadProjectStatusBadgeClasses(l.status)}`}
    >
      {l.status ?? 'Neu'}
    </span>
  );
  return (
    <li
      className={`${disabled ? '' : 'cursor-pointer hover:bg-app-hover active:bg-app-surface-muted/80'} rounded px-2 ${className ?? ''}`}
      onClick={disabled ? undefined : onClick}
      title={disabled ? 'Nicht freigeschaltet' : undefined}
    >
      <div className="sm:hidden flex flex-col gap-1 py-0.5">
        <div className="flex items-start justify-between gap-3">
          <span className="text-sm font-semibold text-app-text truncate leading-snug min-w-0">
            {l.lead_title || '— ohne Titel —'}
          </span>
          {hasPrice ? (
            <span className="text-sm font-semibold tabular-nums text-app-text shrink-0">
              {formatCurrency(hasGross ? gross : net)}
            </span>
          ) : null}
        </div>
        <span className="text-xs text-app-text-subtle leading-snug truncate" title={formatMetaSecondaryLine(l)}>
          {formatMetaSecondaryLine(l)}
        </span>
        <div className="flex items-center justify-between gap-2">
          {statusBadge}
          {hasGross && hasNet ? (
            <span className="text-xs tabular-nums text-app-text-subtle">{formatCurrency(net)}</span>
          ) : null}
        </div>
      </div>
      <div className={`hidden sm:grid ${LEAD_ROW_GRID}`}>
        <div className="col-start-1 row-start-1 min-w-0 self-center">
          <span className="block text-sm font-semibold text-app-text truncate leading-snug">
            {l.lead_title || '— ohne Titel —'}
          </span>
        </div>
        <div className="col-start-2 row-start-1 flex justify-end self-center">{statusBadge}</div>
        {hasPrice &&
          (hasGross && hasNet ? (
            <>
              <div className="col-start-3 row-start-1 text-right text-sm font-semibold tabular-nums text-app-text leading-snug self-end">
                {formatCurrency(gross)}
              </div>
              <div className="col-start-3 row-start-2 text-right text-xs tabular-nums text-app-text-subtle leading-snug self-start">
                {formatCurrency(net)}
              </div>
            </>
          ) : hasGross ? (
            <div className="col-start-3 row-start-1 row-span-2 flex items-center justify-end">
              <span className="text-sm font-semibold tabular-nums text-app-text">{formatCurrency(gross)}</span>
            </div>
          ) : (
            <div className="col-start-3 row-start-1 row-span-2 flex items-center justify-end">
              <span className="text-sm font-semibold tabular-nums text-app-text">{formatCurrency(net)}</span>
            </div>
          ))}
        <div className="col-start-1 row-start-2 min-w-0">
          <span className="block text-xs text-app-text-subtle leading-snug truncate" title={formatMetaSecondaryLine(l)}>
            {formatMetaSecondaryLine(l)}
          </span>
        </div>
      </div>
    </li>
  );
}

function leadMatchesQuery(l: LeadProjectRow, q: string): boolean {
  const tokens = q
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;
  const hay = [
    l.lead_title,
    l.display_id,
    l.status,
    (l.site_address ?? '').trim(),
    l.created_at ? formatDateDe(l.created_at) : '',
    l.offer_net_total != null ? formatCurrency(l.offer_net_total) : '',
    l.offer_gross_total != null ? formatCurrency(l.offer_gross_total) : '',
    ...(l.contact_roles ?? []).includes('site_contact') ? ['baustellenkontakt'] : [],
    ...(l.contact_roles ?? []).includes('project_contact') ? ['auftraggeber'] : [],
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return tokens.every((t) => hay.includes(t));
}

export const LeadProjectsSection: React.FC<{
  leads: LeadProjectRow[];
  headerAction?: React.ReactNode;
}> = ({ leads, headerAction }) => {
  const navigate = useNavigate();
  const { canOpen } = useModuleAccess();
  const projectsOpenable = canOpen(projectsListPath());
  const [modalOpen, setModalOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (!modalOpen) setSearch('');
  }, [modalOpen]);

  const goToLead = (l: LeadProjectRow) => {
    if (!projectsOpenable) return;
    navigate(leadDetailPath({ id: l.id }));
  };

  const preview = leads.slice(0, PREVIEW_LIMIT);
  const showOpenAll = leads.length > PREVIEW_LIMIT;
  const filteredModal = React.useMemo(
    () => leads.filter((l) => leadMatchesQuery(l, search)),
    [leads, search],
  );

  return (
    <>
      <section className="bg-app-surface border-b border-app-border/60 md:border md:border-app-border md:rounded-lg md:shadow-[0_1px_2px_rgba(0,0,0,0.03)] md:overflow-hidden last:border-b-0 md:last:border">
        <header className="px-4 py-3 min-h-11 border-b border-app-border/60 flex items-center justify-between gap-2">
          <div className="flex items-baseline gap-2 min-w-0">
            <h2 className="text-sm font-semibold text-app-text shrink-0">Projekte</h2>
            <span className="text-sm text-app-text-subtle truncate">{leads.length} gesamt</span>
          </div>
          {headerAction ? <div className="shrink-0 flex items-center gap-1">{headerAction}</div> : null}
        </header>
        <div className="px-4 py-3 md:p-4">
          {leads.length === 0 ? (
            <p className="text-sm text-app-text-subtle">Noch keine Projekte.</p>
          ) : (
            <>
              <ul className="divide-y divide-app-border/60">
                {preview.map((l) => (
                  <LeadProjectRowInner key={l.id} l={l} className="py-2" disabled={!projectsOpenable} onClick={() => goToLead(l)} />
                ))}
              </ul>
              {showOpenAll && (
                <div className="mt-3 pt-1 border-t border-app-border/40">
                  <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Alle Projekte anzeigen
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {showOpenAll && (
        <AppModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          overlayClassName="z-[100]"
          panelClassName="md:max-w-lg w-full"
          ariaLabelledBy="lead-projects-modal-title"
        >
        <div className="flex flex-col min-h-0 max-h-[85vh] md:max-h-[90vh]">
          <div className="flex items-start justify-between gap-3 p-4 border-b border-app-border/60 shrink-0">
            <div>
              <h2 id="lead-projects-modal-title" className="text-sm font-semibold text-app-text">
                Projekte
              </h2>
              <p className="text-xs text-app-text-subtle mt-0.5">{leads.length} Einträge</p>
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="p-2 rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
              aria-label="Schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 border-b border-app-border/60 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Titel, Nummer, Datum, Baustelle …"
                className="w-full pl-9 pr-3 py-2 text-sm border border-app-border rounded-lg bg-app-surface focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                autoFocus
              />
            </div>
          </div>
          <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 pb-4">
            {filteredModal.length === 0 ? (
              <p className="text-sm text-app-text-subtle px-2 py-6 text-center">Keine Treffer.</p>
            ) : (
              <ul className="divide-y divide-app-border/60">
                {filteredModal.map((l) => (
                  <LeadProjectRowInner
                    key={l.id}
                    l={l}
                    className="py-2.5 rounded-md"
                    disabled={!projectsOpenable}
                    onClick={() => {
                      setModalOpen(false);
                      goToLead(l);
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </AppModal>
      )}
    </>
  );
};
